/* UserData.js */

'use strict';

const Steve = (require("@gluenetworks/steve_core")).getInstance();
const EventEmitter = require('events');
const _ = require('lodash');

const COLUMN_WIDTH_LABEL = 'columnWidth';
const COLUMN_ORDER_LABEL = 'columnOrder';
const COLUMN_LOCKED_LABEL = 'columnLocked';
const COLUMN_SORTING_LABEL = 'columnSorting';
const BASIC_SEARCH_DATA_LABEL = 'basicSearchData';
const ADVANCED_SEARCH_DATA_LABEL = 'advancedSearchData';
const GROUPING_DATA_LABEL = 'groupingData';

const STORAGE_TYPE_NONE = 'none';
const STORAGE_TYPE_LOCAL = 'local';
const STORAGE_TYPE_SESSION = 'session';
const STORAGE_TYPE_REMOTE = 'remote';

const SESSION_LEVEL_KEYS = [
    BASIC_SEARCH_DATA_LABEL,
    ADVANCED_SEARCH_DATA_LABEL
];

const SHARED_KEYS = [
    BASIC_SEARCH_DATA_LABEL,
    ADVANCED_SEARCH_DATA_LABEL,
    GROUPING_DATA_LABEL
];

/**
 * Obsolete UserGridData class,
 * remove after migrate to storageFactory completes
 */
class UserGridData extends EventEmitter {
    constructor(gridKey, gridModel, options) {
        super();
        this._logger = Steve._di.getRegistered('$teve.logger').registeredObj;
        this.setGridModel(gridModel);

        this._storageName = this.getStorageName(gridKey);
        this._sharedStorageName = this.getSharedStorageName(gridKey);
        if (!gridKey) {
            this._logger.info(`UserGridData: Missing gridKey parameter: userData will be temporarily stored in "${this._storageName}" section`);
        }

        // Initialise by loadStorage()
        this._storage = undefined;
        this._storageData = undefined;
        this._storageType = _.get(options, 'storageType', STORAGE_TYPE_REMOTE);
        // Steve user services - Only required for remote storage
        this._userService = undefined;
        this._userSession = undefined;
        this._userId = undefined;

        this._resizeHandler = this._resizeHandler.call(this, 300);
    }

    get storageName() {
        return this._storageName;
    }

    get storage() {
        return this._storage;
    }

    get storageData() {
        return this._storageData;
    }

    setGridModel(gridModel) {
        if (!gridModel) {
            this._logger.info("UserGridData: Missing gridModel parameter: Event listeners will be disabled");
            return;
        }

        if (this._gridModel) {
            this._unregisterListeners();
        }

        this._gridModel = gridModel;
        this._registerListeners();
    }

    whenStorageIsReady(force = false) {
        return this.loadStorage(this._storageType, force);
    }

    _registerListeners() {
        if (this._gridModel) {
            this._gridModel.on('UserSorted', this._onUserSorted = this._onUserSorted.bind(this));
            this._gridModel.on('ColumnUserResized', this._onColumnUserResized = this._onColumnUserResized.bind(this));
            this._gridModel.on('LockedColumns', this._onLockedColumns = this._onLockedColumns.bind(this));
            this._gridModel.on('UserReordered', this._onUserReordered = this._onUserReordered.bind(this));
            this._gridModel.on('ColumnOrder', this._onColumnOrderChange = this._onColumnOrderChange.bind(this));
            this._gridModel.on('BasicSearchFilterChangeData', this._onBasicSearchChangeData = this._onBasicSearchChangeData.bind(this));
            this._gridModel.on('AdvancedSearchFilterChangeData', this._onAdvancedSearchChangeData = this._onAdvancedSearchChangeData.bind(this));
            this._gridModel.on('GroupingChanged', this._onGroupingChange = this._onGroupingChange.bind(this));
        }
        if (this._storageType === STORAGE_TYPE_LOCAL) {
            this._registerLocalStorageListeners();
        }
    }

    _registerLocalStorageListeners() {
        window.addEventListener('storage', this._onStorageUpdated = this._onStorageUpdated.bind(this));
    }

    _unregisterListeners() {
        if (this._gridModel) {
            this._gridModel.removeListener('UserSorted', this._onUserSorted);
            this._gridModel.removeListener('ColumnUserResized', this._onColumnUserResized);
            this._gridModel.removeListener('LockedColumns', this._onLockedColumns);
            this._gridModel.removeListener('UserReordered', this._onUserReordered);
            this._gridModel.removeListener('ColumnOrder', this._onColumnOrderChange);
            this._gridModel.removeListener('BasicSearchFilterChangeData', this._onBasicSearchChangeData);
            this._gridModel.removeListener('AdvancedSearchFilterChangeData', this._onAdvancedSearchChangeData);
            this._gridModel.removeListener('GroupingChanged', this._onGroupingChange);
        }
        if (this._storageType === STORAGE_TYPE_LOCAL) {
            this._unregisterLocalStorageListeners();
        }
    }

    _unregisterLocalStorageListeners() {
        window.removeEventListener('storage', this._onStorageUpdated);
    }

    shutdown() {
        this._storage.removeItem(this.getStorageName());
        this.removeGlobalResetMethod();
        return new Promise((resolve) => {
            this._unregisterListeners();
            this._unregisterLocalStorageListeners();
            this._gridModel = undefined;
            this._storageName = undefined;
            this._storage = undefined;
            this._storageData = undefined;
            this._storageType = undefined;
            this._userService = undefined;
            this._userSession = undefined;
            this._userId = undefined;
            return resolve();
        });
    }

    getData(key) {
        let storageData = null;
        if (SHARED_KEYS.includes(key)) {
            storageData = this.getStorageData(this._sharedStorageName);
        } else {
            storageData = this.getStorageData(this._storageName);
        }

        if (!storageData) {
            return;
        }

        if (!key) {
            return storageData;
        }
        const keyData = storageData[key];

        if (SESSION_LEVEL_KEYS.includes(key) && this._userSession && Object(keyData).hasOwnProperty('sessionToken')) {
            return keyData.sessionToken === this._userSession.token ? keyData.value : '';
        }

        return keyData;
    }

    getSorting() {
        return this.getData(COLUMN_SORTING_LABEL) || {};
    }

    getColOrder() {
        return this.getData(COLUMN_ORDER_LABEL) || [];
    }

    getLocked() {
        return this.getData(COLUMN_LOCKED_LABEL) || [];
    }

    getColWidth() {
        return this.getData(COLUMN_WIDTH_LABEL) || {};
    }

    getBasicSearchData() {
        return this.getData(BASIC_SEARCH_DATA_LABEL) || {};
    }
    getAdvancedSearchData() {
        return this.getData(ADVANCED_SEARCH_DATA_LABEL) || {};
    }

    /**
     * @typedef {Object} GroupingData - Grouping data object
     * @property {string|undefined} groupBy - the group by column name
     * @property {string[]} - list of expanded groups
     */

    /**
     * Function to get stored grouping data
     * @returns {GroupingData}
     */
    getGroupingData() {
        return this.getData(GROUPING_DATA_LABEL) || ({ groupBy: undefined, expanded: [], prevColStatus: undefined });
    }

    setData(data, key, force = false) {
        const keyValueData = {};
        let storageName = SHARED_KEYS.includes(key) ? this._sharedStorageName : this._storageName;

        if (key && typeof key === 'string') {
            if (SESSION_LEVEL_KEYS.includes(key) && this._userSession) {
                keyValueData[key] = {
                    value: data,
                    sessionToken: this._userSession.token
                };

            } else {
                keyValueData[key] = data;
            }
        }

        const userData = force ? data : Object.assign({}, this._storageData, keyValueData);
        this._storageData = userData;

        this.updateStorage(keyValueData, storageName);
    }

    updateStorage(keyValueData, storageName) {
        const storage = this.getStorageData(storageName);
        let data = Object.assign({}, storage, keyValueData);

        if (typeof data !== 'string') {
            data = JSON.stringify(data);
        }
        this._storage.setItem(storageName, data);
    }

    updateGrid(keys = []) {
        keys.forEach(key => {
            switch (key) {
                case COLUMN_WIDTH_LABEL:
                    return this.emit('widthChanged', this._storageData[key]);
                case COLUMN_ORDER_LABEL:
                    return this.emit('orderChanged', this._storageData[key]);
                case COLUMN_LOCKED_LABEL:
                    return this.emit('lockedChanged', this._storageData[key]);
                case COLUMN_SORTING_LABEL:
                    return this.emit('sortingChanged', this._storageData[key]);
                default:
                    return;
            }
        });
    }

    _onColumnUserResized(columnResizeData) {
        this._resizeHandler(columnResizeData);
    }

    _resizeHandler(delay) {
        return _.debounce(columnResizeData => {
            if (!columnResizeData || !columnResizeData.column) {
                return;
            }
            const columnResized = {};

            columnResized[columnResizeData.column] = columnResizeData.width;
            this.setData(
                Object.assign({}, this.getData(COLUMN_WIDTH_LABEL), columnResized),
                COLUMN_WIDTH_LABEL
            );
        }, delay);
    }

    _onUserSorted(sortData) {
        const sorting = {};
        const sortingCriteria = _.get(this._gridModel, ['_columns', sortData.sortColumn, 'path'], sortData.sortColumn);
        sorting[sortingCriteria] = (sortData.sort === 'asc') ? 1 : -1;
        this.setData(sorting, COLUMN_SORTING_LABEL);
    }

    _onLockedColumns(lockedColumns) {
        const updatedColumnsList = this._updateColumnsList(lockedColumns, this.getLocked());
        this.setData(updatedColumnsList, COLUMN_LOCKED_LABEL);
    }

    _onUserReordered(colReorder) {
        const columnOrder = colReorder.columnOrder;
        const updatedColumnsList = this._updateColumnsList(columnOrder, this.getColOrder());
        this.setData(updatedColumnsList, COLUMN_ORDER_LABEL);
    }

    _onColumnOrderChange(columnOrder) {
        const updatedColumnsList = this._updateColumnsList(columnOrder, this.getColOrder());
        this.setData(updatedColumnsList, COLUMN_ORDER_LABEL);
    }

    _onBasicSearchChangeData(filterData) {
        this.setData(filterData, BASIC_SEARCH_DATA_LABEL);
    }

    _onAdvancedSearchChangeData(filterQuery) {
        this.setData(filterQuery, ADVANCED_SEARCH_DATA_LABEL);
    }

    /**
     *
     * @param {Object} groupingData -
     * @param {string} groupingData.groupBy - Group by column name
     * @param {string[]} groupingData.expanded - list of expanded groups
     *
     * @private
     */
    _onGroupingChange(groupingData) {
        this.setData(groupingData, GROUPING_DATA_LABEL);
    }

    /**
     * Function to keep all custom fields stored even if not available in active organization
     * @returns {Array}
     */
    _updateColumnsList(columnOrder, storedColumns) {
        const gridColumns = Object.keys(this._gridModel.getColumns() || {});
        const columnsDifference = _.difference(storedColumns, gridColumns);

        const updatedColumnOrder = columnsDifference.reduce((updatedOrder, colName) => {
            updatedOrder.splice(storedColumns.indexOf(colName), 0, colName);
            return updatedOrder;
        }, [...columnOrder]);

        return _.uniq(updatedColumnOrder.filter(Boolean));
    }

    _onStorageUpdated(eventData) {
        if (eventData.key !== this.storageName) {
            return;
        }
        let newStorage = this._storageData;
        try {
            newStorage = JSON.parse(eventData.newValue) || {};
        } catch (e) {
            console.warn('Invalid data from the storage');
        }

        const keysUpdated = this._checkDiff(this._storageData, newStorage);
        this.setData(newStorage, null, true);
        this.updateGrid(keysUpdated);
    }

    _checkDiff(currentStorage, newStorage) {
        const keysUpdated = [];
        if (!newStorage) {
            return keysUpdated;
        }
        Object.keys(newStorage).forEach(key => {
            if (!currentStorage.hasOwnProperty(key)) {
                return keysUpdated.push(key);
            }
            const newValue = newStorage[key];
            if (Array.isArray(newValue)) {
                if (newValue.length !== currentStorage[key].length) {
                    return keysUpdated.push(key);
                }
                const isEq = newValue.every((val, idx) => val === currentStorage[key][idx]);
                if (!isEq) {
                    return keysUpdated.push(key);
                }
            } else if (Object(newValue) === newValue) {
                const newValueKeys = Object.keys(newValue);
                if (newValueKeys.length !== Object.keys(currentStorage[key]).length) {
                    return keysUpdated.push(key);
                }
                const isEq = newValueKeys.every(objectKey => newValue[objectKey] === currentStorage[key][objectKey]);
                if (!isEq) {
                    return keysUpdated.push(key);
                }

            }
        });
        return keysUpdated;
    }

    getStorageName(key = 'tmpSection') {
        return `${key}_userData`;
    }

    getSharedStorageName(key) {
        //assuming that gridKey is in format '<solution_name>_<grid_name>',
        //shared storage name will be 'all_<grid_name>'

        const grid_name = String(key).split('_')[1];
        return grid_name ? `all_${grid_name}` : this._storageName;
    }

    loadStorage(storageType = STORAGE_TYPE_REMOTE, force = false) {
        if (storageType === this._storageType && this._storage && !force) {
            return Promise.resolve(this._storage);
        }

        let storage;
        this._storageType = storageType;
        switch (storageType) {
            case STORAGE_TYPE_LOCAL:
                storage = this.setUpBrowserStorage(window.localStorage);
                break;

            case STORAGE_TYPE_SESSION:
                storage = this.setUpBrowserStorage(window.sessionStorage);
                break;

            case STORAGE_TYPE_REMOTE:
                storage = this.setUpRemoteStorage();
                break;

            case STORAGE_TYPE_NONE:
                this._logger.info("UserGridData: 'None' storage type requested");
                storage = this.storageFallback();
                break;

            default:
                this._storageType = STORAGE_TYPE_NONE;
                storage = this.storageFallback("UserGridData: Unknown storage type specified");
        }

        return storage.then(storage => {
            this._storage = storage;

            const storageData = this.getStorageData(this.storageName);

            this._storage.setItem(this.storageName, JSON.stringify(storageData));
            this._storageData = Object.assign({},
                this.getStorageData(this._storageName),
                this.getStorageData(this._sharedStorageName)
            );

            this.setGlobalResetMethod(storage);

            return storage;
        });
    }

    getStorageData(storageName = this.storageName) {
        let storageData;

        try {
            storageData = this._storage.getItem(storageName) || '{}';
            storageData = JSON.parse(storageData);
        } catch (e) {
            console.warn("Can't get storage data, for storage - " + storageName);
            storageData = {};
        }
        return storageData;
    }

    setUpBrowserStorage(windowStorage) {

        let storage = windowStorage;
        const testKey = "key_" + performance.now();
        const testVal = "value_" + performance.now();

        if (!(storage instanceof Storage)) {
            this._storageType = STORAGE_TYPE_NONE;
            storage = this.storageFallback();
        }

        try {
            storage.setItem(testKey, testVal);
            const dataIsEqual = storage.getItem(testKey) === testVal;
            storage.removeItem(testKey);
            if (!dataIsEqual) {
                this._storageType = STORAGE_TYPE_NONE;
                storage = this.storageFallback();
            }
        } catch (err) {
            this._storageType = STORAGE_TYPE_NONE;
            storage = this.storageFallback(err);
        }

        return Promise.resolve(storage);
    }

    setUpRemoteStorage() {
        try {
            this._userService = Steve.di.getRegistered('$teve.services.User').registeredObj;
            this._userSession = Steve.di.getRegistered('$teve.userSession').registeredObj;
            this._userId = this._userSession._userEntity.id;
        } catch (err) {
            this._storageType = STORAGE_TYPE_NONE;
            return this.storageFallback(`UserGridData: Remote storage cannot be initialised: ${err.message}`);
        }

        return this._userService.retrievePreferences({ userId: this._userId })
            .then((result) => {
                const storePreferences = this._userService.storePreferences;
                const updatePreferences = this._userService.updatePreferences;
                const userId = this._userId;

                let userPreferences = result || {};
                let storageRemoteData = userPreferences.entity_grid_gl || {};

                let saveItem = (data) => {
                    data = _.isUndefined(data) ? storageRemoteData : data;

                    let request = {
                        userId: userId,
                        prefObj: {
                            entity_grid_gl: {}
                        }
                    };
                    storageRemoteData = data;
                    request.prefObj.entity_grid_gl = JSON.parse(JSON.stringify(data));
                    storePreferences(request)
                        .catch((err) => {
                            this._logger.error({ error: err }, err, 'UserGridData: storePreferences - Error');
                            return Promise.reject(err);
                        });
                };

                const updateItem = (key, value = "", operator = '$set') => {
                    if (!key) {
                        return Promise.resolve({});
                    }
                    return updatePreferences({
                            userId: userId,
                            operator: operator,
                            docKey: `entity_grid_gl.${key}`,
                            docValue: value
                        })
                        .catch((err) => {
                            this._logger.error({ error: err }, err, `UserGridData: Update ${key} Preferences Error`);
                            return err;
                        });
                };


                const setItem = (() => {
                    let updateItemPromise = Promise.resolve();

                    /*
                    //set Item could be called for StorageName and  SharedStorageName keys
                    //consider if it is better to use debounced and memoized functions by the key;
                    const memoizedAndDebouncedSetItem = _.memoize(key => _.debounce(value => {
                        updateItemPromise = updateItemPromise.then(() => updateItem(key, value));
                    }, 0));
                    */

                    return (key, value) => {
                        storageRemoteData[key] = value;
                        //memoizedAndDebouncedSetItem(key)(value);
                        updateItemPromise = updateItemPromise.then(() => updateItem(key, value));
                        return updateItemPromise;
                    };
                })();

                const getItem = key => storageRemoteData[key];

                const removeItem = key => {
                    updateItem(key, '', '$unset');
                };

                const resetData = () => {
                    saveItem({});
                };

                return Promise.resolve({
                    setItem: setItem,
                    getItem: getItem,
                    removeItem: removeItem,
                    resetData: resetData
                });
            })
            .catch((err) => {
                this._storageType = STORAGE_TYPE_NONE;
                return this.storageFallback(`UserGridData: Remote storage cannot be loaded: ${err.message}`);
            });
    }

    storageFallback(errMsg) {
        window.gridStorage = window.gridStorage || {};

        const storage = window.gridStorage;
        const setItem = (key, value) => {
            storage[key] = value;
        };
        const getItem = key => storage[key];
        const removeItem = key => {
            delete storage[key];
        };

        if (errMsg) {
            this._logger.error({ error: errMsg }, `UserGridData: ${errMsg}`);
        }

        console.warn("UserGridData: User's grid preferences will not be saved");

        return Promise.resolve({
            setItem: setItem,
            getItem: getItem,
            removeItem: removeItem
        });
    }

    setGlobalResetMethod(storage) {
        const resetGridData = window.resetGridData = window.resetGridData || {};
        const storageName = this.storageName;

        resetGridData[storageName] = () => {
            storage.removeItem(storageName);
        };

        if (storage.resetData) {
            resetGridData.resetRemoteData = () => storage.resetData();
        }
    }
    removeGlobalResetMethod() {
        if (window.resetGridData) {
            window.resetGridData = _.omit(window.resetGridData, this.storageName);
        }
    }
}

module.exports = UserGridData;